/**
 * Shared detection for zkLogin failures that only a fresh OAuth round-trip can
 * fix.
 *
 * The auto-recovery inside `signAndExecuteTransaction` already retries the proof
 * once. If we still see one of these messages afterwards, the session is
 * irrecoverable from JS and the only honest thing to show the user is a "reset
 * login" affordance.
 *
 * This deliberately lives outside any component. It started life inside
 * Dashboard.tsx, which meant the dashboard degraded gracefully while every other
 * surface that signs a transaction — notably shop registration — dead-ended on a
 * raw fullnode string like:
 *
 *   Invalid user signature: Signature is not valid: Signature is not valid:
 *   General cryptographic error: Groth16 proof verify failed
 *
 * A café owner hitting that at signup has no idea they simply need to log in
 * again, and "TRY AGAIN" loops forever because the stale proof is reused.
 */
export function isSessionError(err: unknown): boolean {
  const msg = ((err as any)?.message || String(err ?? '')).toLowerCase();
  return (
    msg.includes('zk proof') ||
    msg.includes('please log in again') ||
    msg.includes('session expired') ||
    msg.includes('ephemeral keypair missing') ||
    msg.includes('max epoch') ||
    // Real rejections come back from the fullnode as raw signature errors, not
    // our friendly strings. A stale proof surfaces as "Groth16 proof verify
    // failed" wrapped in "Invalid user signature: Signature is not valid".
    msg.includes('invalid signature') ||
    msg.includes('invalid user signature') ||
    msg.includes('signature is not valid') ||
    msg.includes('groth16') ||
    (msg.includes('epoch') && msg.includes('expire'))
  );
}

export default isSessionError;
